import { Controller } from "@hotwired/stimulus"

import SearchMapController from "./search_map_controller"

import { saveNavigationTypeToStorage, selectContentEvent } from "@/helpers/ga4_helper"
import { urlWithBounds } from "@/helpers/search_map"

export default class PriceFilterController extends Controller<HTMLDivElement> {
  static outlets = ["search-map"]
  declare readonly searchMapOutlet: SearchMapController

  static targets = [
    "lowerInput",
    "lowerLabel",
    "upperInput",
    "upperLabel",
    "activeTrack",
    "priceLink"
  ]
  declare readonly lowerInputTarget: HTMLInputElement
  declare readonly lowerLabelTarget: HTMLSpanElement
  declare readonly upperInputTarget: HTMLInputElement
  declare readonly upperLabelTarget: HTMLSpanElement
  declare readonly activeTrackTarget: HTMLDivElement
  declare readonly priceLinkTarget: HTMLAnchorElement

  static values = {
    max: Number,
    step: Number
  }
  declare stepValue: number
  declare maxValue: number

  connect(): void {
    this.initializeStyles()
  }

  applyFilter(): void {
    const href = this.isWholeRangeCovered ? this.element.dataset.urlForRemove! : this.urlForApply
    const url = urlWithBounds(href, this.searchMapOutlet.mapBounds)

    if (url.pathname === window.location.pathname) {
      return
    }

    this.priceLinkTarget.setAttribute("href", url.toString())
    this.priceLinkTarget.click()

    selectContentEvent("Search Filters", this.selectedRangeAsString(), "Price Filter")
    saveNavigationTypeToStorage("filtered")
  }

  lowerPriceChanged(e: Event): void {
    const input = e.target as HTMLInputElement
    const value = +input?.value

    e.preventDefault()
    this.updateLowerPrice(value)
  }

  lowerPriceLabelBlurred(e: KeyboardEvent): void {
    this.priceLabelBlurred(e, this.lowerInputTarget)
  }

  lowerPriceLabelChanged(e: KeyboardEvent): void {
    this.priceLabelChanged(e, this.lowerInputTarget)
  }

  upperPriceChanged(e: Event): void {
    const input = e.target as HTMLInputElement
    const value = +input?.value
    if (!value) return

    e.preventDefault()
    this.updateUpperPrice(value)
  }

  upperPriceLabelBlurred(e: KeyboardEvent): void {
    this.priceLabelBlurred(e, this.upperInputTarget)
  }

  upperPriceLabelChanged(e: KeyboardEvent): void {
    this.priceLabelChanged(e, this.upperInputTarget)
  }

  private initializeStyles(): void {
    const lowerValue = +this.lowerInputTarget.value
    const upperValue = +this.upperInputTarget.value
    this.setLowerLabel(lowerValue, upperValue)
    this.setUpperLabel(upperValue, lowerValue)
    this.setActiveTrack(lowerValue, upperValue)
  }

  private isProhibitedKey(key: string): boolean {
    const allowedKeys = ["ArrowLeft", "ArrowRight", "Backspace"]
    const isNonDigit = /\D/.test(key)
    const isAllowedKey = allowedKeys.some((k) => k === key)

    return isNonDigit && !isAllowedKey
  }

  private get isWholeRangeCovered(): boolean {
    return +this.lowerInputTarget.value === 0 && +this.upperInputTarget.value === this.maxValue
  }

  private priceLabelBlurred(e: Event, input: HTMLInputElement): void {
    const editableElement = e.target as HTMLSpanElement
    const value = editableElement.textContent || input.value
    const cleanedValue = Math.min(this.maxValue, +value)
    const hasChanged = +input.value !== cleanedValue

    if (!hasChanged) {
      return
    }

    if (editableElement === this.lowerLabelTarget) {
      this.updateLowerPrice(cleanedValue)
    } else {
      this.updateUpperPrice(cleanedValue)
    }

    this.applyFilter()
  }

  private priceLabelChanged(e: KeyboardEvent, input: HTMLInputElement): void {
    const editableElement = e.target as HTMLSpanElement

    if (e.key === "Enter" || e.key === "Tab") {
      editableElement.blur()
    } else if (e.key === "Escape") {
      e.stopPropagation()
      editableElement.textContent = input.value
      editableElement.blur()
    } else if (this.isProhibitedKey(e.key)) {
      e.preventDefault()
    }
  }

  private selectedRangeAsString(): string {
    return `${this.lowerInputTarget.value}-${this.upperInputTarget.value}`
  }

  private setActiveTrack(lowerValue: number, upperValue: number): void {
    const left = (100 * lowerValue) / this.maxValue
    const width = (100 * (upperValue - lowerValue)) / this.maxValue

    this.activeTrackTarget.setAttribute(
      "style",
      `display:block;left:calc(${left}%);width:calc(${width}%);`
    )
  }

  private setLabel(value: number, label: HTMLSpanElement, opposingValue: number): void {
    const valuePct = value / this.maxValue
    const opposingPct = opposingValue / this.maxValue

    const labelGap = 2

    const labelWidth = this.getLabelWidth(value)
    const opposingLabelWidth = this.getLabelWidth(opposingValue)

    let left: string

    if (label === this.lowerLabelTarget) {
      // prevent left label from overlapping the right label and from going off the left edge
      const calculatedLeft = `calc(${valuePct * 100}% - ${labelWidth}px)`

      const opposingLeftLimit = `min(calc(${opposingPct * 100}%), calc(100% - ${opposingLabelWidth}px))`
      const maxLeftBeforeOverlap = `calc(${opposingLeftLimit} - ${labelWidth}px - ${labelGap}px)`

      left = `max(min(${calculatedLeft}, ${maxLeftBeforeOverlap}), 0px)`
    } else {
      // prevent right label from overlapping the left label and from going off the right edge
      const calculatedLeft = `calc(${valuePct * 100}%)`

      const opposingRightLimit = `max(calc(${opposingPct * 100}% - ${opposingLabelWidth}px), 0px)`
      const minLeftAfterOverlap = `calc(${opposingRightLimit} + ${opposingLabelWidth}px + ${labelGap}px)`

      left = `min(max(${calculatedLeft}, ${minLeftAfterOverlap}), calc(100% - ${labelWidth}px))`
    }

    label.setAttribute("style", `display:inline-block; left:${left};`)
    label.textContent = value.toString()
  }

  private getLabelWidth(value: number): number {
    const avgCharWidth = 10 // average character width in px
    const labelPadding = 20 // padding around label in px

    return (String(value).length + 1) * avgCharWidth + labelPadding
  }

  private setLowerLabel(value: number, upperValue: number): void {
    this.setLabel(value, this.lowerLabelTarget, upperValue)
  }

  private setUpperLabel(value: number, lowerValue: number): void {
    this.setLabel(value, this.upperLabelTarget, lowerValue)
  }

  private updateLowerPrice(value: number): void {
    const newLowerValue = Math.min(value, this.maxValue - this.stepValue)
    const upperValue = +this.upperInputTarget.value

    this.lowerInputTarget.value = newLowerValue.toString()
    this.setLowerLabel(newLowerValue, upperValue)

    if (newLowerValue >= upperValue) {
      const newUpperValue = Math.max(newLowerValue + this.stepValue, upperValue)
      this.updateUpperPrice(newUpperValue)
    } else {
      this.setActiveTrack(newLowerValue, upperValue)
    }
  }

  private updateUpperPrice(value: number): void {
    const newUpperValue = Math.max(value, this.stepValue)
    const lowerValue = +this.lowerInputTarget.value

    this.upperInputTarget.value = newUpperValue.toString()
    this.setUpperLabel(newUpperValue, lowerValue)

    if (newUpperValue <= lowerValue) {
      const newLowerValue = Math.min(newUpperValue - this.stepValue, lowerValue)
      this.updateLowerPrice(newLowerValue)
    } else {
      this.setActiveTrack(lowerValue, newUpperValue)
    }
  }

  private get urlForApply(): string {
    const { urlPrefix, urlSuffix } = this.element.dataset

    return `${urlPrefix}${this.selectedRangeAsString()}${urlSuffix}`
  }
}
